import React, { useEffect, useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, MenuItem, Typography, FormControl
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { SelectPlaceholder } from 'asterX-UI/Inputs'
import { enqueueSnackbar } from 'notistack'
import { CreditFlow } from 'lib/api/services/creditFlow.service'

const defaultData = {
  id: '',
  name: '',
  code: '',
  cityId: null,
  cityName: null,
  dealerGroupCode: null
}

const AdminModal = ({ open, mode = 'view', tab, data = {}, onClose, onSave }) => {
  const [formData, setFormData] = useState(defaultData)
  const [cities, setCities] = useState([])
  const [groups, setGroups] = useState([])
  const [errors, setErrors] = useState({})
  const isReadOnly = mode === 'view'

  useEffect(() => {
    if (mode === 'create') {
      setFormData(defaultData)
    } else {
      setFormData({
        id: data.id ?? '',
        name: data.name ?? '',
        code: data.code ?? '',
        cityId: data.cityId ?? null,
        cityName: data.cityName ?? null,
        dealerGroupCode: data.dealerGroupCode ?? null
      })
    }
    setErrors({})
  }, [data, mode])

  useEffect(() => {
    if (tab === 'dealers') {
      CreditFlow.getCitiesDropdown()
        .then(res => setCities(res.data || []))
        .catch(() => setCities([]))
      CreditFlow.getDealerGroupsFilter({ offset: 0, limit: 100 })
        .then(res => setGroups(res.data?.list || []))
        .catch(() => setGroups([]))
    }
  }, [tab])

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(f => ({ ...f, [name]: value }))
  }

  const validate = () => {
    const err = {}
    if (!formData.name?.trim()) err.name = true
    if ((tab === 'groups' || tab === 'dealers') && !formData.code?.trim()) err.code = true
    if (tab === 'dealers') {
      if (!formData.cityId) err.cityId = true
      if (!formData.dealerGroupCode) err.dealerGroupCode = true
    }
    setErrors(err)
    if (Object.keys(err).length) {
      enqueueSnackbar('Заполните выделенные поля', { variant: 'error' })
      return false
    }
    return true
  }

  const handleSubmit = () => {
    if (mode === 'delete') {
      onSave?.(formData)
      onClose()
      return
    }
    if (!validate()) return
    onSave?.(formData)
    onClose()
  }

  const renderAdditionalFields = () => {
    if (tab !== 'dealers') return null
    return (
      <>
        <Typography fontWeight={700} mt={2}>Дополнительные поля</Typography>

        <FormControl fullWidth margin="normal" error={!!errors.cityId}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Город
          </Typography>
          <SelectPlaceholder
            placeholder={formData.cityName || 'Нет данных'}
            name="cityId"
            value={formData.cityId ?? ''}
            onChange={e => {
              const val = e.target.value
              const idNum = val === '' ? null : Number(val)
              const sel = cities.find(c => c.id === idNum)
              setFormData(f => ({
                ...f,
                cityId: idNum,
                cityName: sel?.name || null
              }))
            }}
            disabled={isReadOnly}
            IconComponent={props => <ExpandMoreIcon {...props} />}
          >
            <MenuItem value="">Не выбрано</MenuItem>
            {cities.map(c => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </SelectPlaceholder>
        </FormControl>

        <FormControl fullWidth margin="normal" error={!!errors.dealerGroupCode}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Группа дилеров
          </Typography>
          <SelectPlaceholder
            placeholder={formData.dealerGroupCode || 'Нет данных'}
            name="dealerGroupCode"
            value={formData.dealerGroupCode ?? ''}
            onChange={e => {
              const val = e.target.value
              setFormData(f => ({
                ...f,
                dealerGroupCode: val === '' ? null : val
              }))
            }}
            disabled={isReadOnly}
            IconComponent={props => <ExpandMoreIcon {...props} />}
          >
            <MenuItem value="">Не выбрано</MenuItem>
            {groups.map(g => (
              <MenuItem key={g.id} value={g.code}>{g.name}</MenuItem>
            ))}
          </SelectPlaceholder>
        </FormControl>
      </>
    )
  }

  if (mode === 'delete') {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
        <DialogTitle>Удалить запись?</DialogTitle>
        <DialogContent>
          <Typography>
            Вы действительно хотите удалить запись{formData.name ? ` «${formData.name}» ` : ''}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Назад</Button>
          <Button color="error" variant="contained" onClick={handleSubmit}>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {mode === 'create' ? 'Создание записи'
          : mode === 'edit' ? 'Редактирование записи'
            : 'Просмотр записи'}
      </DialogTitle>
      <DialogContent>
        {tab !== 'locations' && mode !== 'create' && (
          <TextField
            fullWidth
            margin="normal"
            label="ID"
            placeholder="ID"
            name="id"
            value={formData.id}
            InputProps={{ readOnly: true }}
            disabled
          />
        )}
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Название
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Название"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          disabled={isReadOnly}
          error={!!errors.name}
          InputLabelProps={{
            shrink: true,
            sx: {
              color: 'text.primary',
              '&.Mui-disabled': { color: 'text.disabled' },
              '&.Mui-focused': { color: 'primary.main' }
            }
          }}
        />

        {(tab === 'dealers' || tab === 'groups') && (
          <>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Код
          </Typography>
          <TextField
            fullWidth
            placeholder="Код"
            name="code"
            value={formData.code}
            onChange={handleChange}
            required
            disabled={isReadOnly}
            error={!!errors.code}
          />
          </>
        )}
        {renderAdditionalFields()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Вернуться к списку</Button>
        {mode !== 'view' && (
          <Button onClick={handleSubmit} variant="contained">Сохранить</Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default AdminModal
